import { useEffect, useRef } from 'react';
import useBrandSettings from './useBrandSettings';

/**
 * Force-opens the brand SchoolSelector (via the global `open-school-selector`
 * event that every brand Header listens for) when the customer lands on a
 * product / package configurator page without a stored school + class context —
 * but only when the brand has the school selector enabled.
 *
 * Fires once per mount. No-op while brand settings are still loading, when the
 * selector is disabled for the brand, when a selection already exists, or when
 * `skip` is set (e.g. an admin / customer order edit, which carries its own
 * school/shop/class scope and must not be interrupted).
 *
 * Also gates the bare configurators under the "Disable Open Shop" brand flag:
 * on a selector-less brand (no popup to force) with the open shop disabled and
 * no stored selection, school-less configuring is not allowed — the page
 * should render the ClosedShopMessage instead. That state is returned as
 * `openShopBlocked` (selector-enabled brands are never blocked: the forced
 * popup handles them).
 *
 * @param {object}  args
 * @param {string}  args.brandSlug     Brand API slug (e.g. 'clsdk').
 * @param {boolean} args.hasSelection  Whether a usable school+class context exists
 *                                     (drives the popup prompt).
 * @param {boolean} [args.hasSchool]   Whether a school alone is known — the block
 *                                     only cares about the school (a missing class
 *                                     is not school-less shopping). Falls back to
 *                                     `hasSelection` when not given.
 * @param {boolean} [args.skip]        Suppress the prompt (order-edit flows, etc.).
 * @param {boolean} [args.urlScoped]   The page's school scope comes from the URL
 *                                     itself (school-shop variation routes) — it is
 *                                     NOT school-less shopping, so never block it
 *                                     even without a stored selection.
 * @param {boolean} [args.mandatory]   Open the popup in NON-DISMISSIBLE mode (the
 *                                     Header passes dismissible={false}): the
 *                                     customer must complete school+shop+class
 *                                     before interacting further. Used by CLS-DK
 *                                     product pages so no add-to-cart happens
 *                                     without full identification.
 * @param {?string} [args.staySchool]  When set (with mandatory), the Header stays
 *                                     on the current page after a selection whose
 *                                     school matches this slug (instead of
 *                                     navigating to the school shop) — the
 *                                     customer keeps configuring the product they
 *                                     came for. Pass the variation URL's school
 *                                     slug, or '*' to stay for any school.
 * @returns {{openShopBlocked: boolean}}
 */
export default function useForceSchoolSelection({ brandSlug, hasSelection, hasSchool, skip = false, urlScoped = false, mandatory = false, staySchool = null, prefill = null }) {
  const { showSchoolSelector, openShopEnabled, loading } = useBrandSettings(brandSlug);
  const firedRef = useRef(false);

  useEffect(() => {
    if (skip || loading || !showSchoolSelector || hasSelection || firedRef.current) {
      return;
    }
    firedRef.current = true;
    // `prefill` ({schoolSlug, shopSlug} read from a school-shop URL) lets the
    // popup open with the school + shop already chosen, asking only for the
    // class — the URL already told us the rest.
    const detail = {
      ...(mandatory ? { mandatory: true, staySchool } : {}),
      ...(prefill?.schoolSlug ? { prefill } : {}),
    };
    document.dispatchEvent(new CustomEvent('open-school-selector', {
      detail: Object.keys(detail).length ? detail : undefined,
    }));
  }, [skip, loading, showSchoolSelector, hasSelection, mandatory, staySchool, prefill?.schoolSlug, prefill?.shopSlug]);

  const openShopBlocked = !skip && !urlScoped && !loading
    && !openShopEnabled && !showSchoolSelector && !(hasSchool ?? hasSelection);
  return { openShopBlocked };
}
